import React from 'react';
import {
  StyledCard,
  StyledEmptyHeaderRow,
  StyledHeader,
  StyledRow,
  StyledTable,
  StyledTableContainer,
  StyledTbody,
  StyledTd,
  StyledTh,
  StyledThead,
  StyledTotalRow,
  StyledTotalTd,
} from './styles';
import { Typography } from '@mui/material';
import { useTranslations } from 'hooks/useTranslations';

export interface DashboardCardRow {
  name: string;
  columns: number[];
}

export interface DashboardCardProps {
  title: string;
  columnHeaders?: string[];
  rows: DashboardCardRow[];
  totalColumn?: boolean;
  firstEmptyRow?: boolean;
  lastEmptyRow?: boolean;
}

export const DashboardCard: React.FC<DashboardCardProps> = ({
  title,
  columnHeaders,
  rows,
  totalColumn = false,
  firstEmptyRow = false,
  lastEmptyRow = false,
}) => {
  const { t } = useTranslations();
  const calculateColumnTotal = (columnIndex: number): number => {
    return rows.reduce((sum, row) => sum + (row.columns[columnIndex] || 0), 0);
  };

  const columnsCount = rows[0]?.columns.length || 0;
  const columnTotals = Array.from({ length: columnsCount }, (_, index) =>
    calculateColumnTotal(index),
  );

  return (
    <StyledCard>
      <StyledHeader>
        <Typography variant="subtitle1">{title}</Typography>
      </StyledHeader>
      <StyledTable>
        <StyledThead>
          {columnHeaders && columnHeaders.length > 0 ? (
            <tr>
              <StyledTh></StyledTh>
              {columnHeaders.map((header, index) => (
                <StyledTh
                  key={index}
                  style={{
                    paddingRight:
                      index + 1 === columnHeaders.length && !totalColumn ? '20px' : '5px',
                  }}
                >
                  <Typography variant="body2">{t(header)}</Typography>
                </StyledTh>
              ))}
              {totalColumn && (
                <StyledTh
                  key={columnHeaders.length}
                  style={{
                    paddingRight: '20px',
                  }}
                >
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {t('global.total.label')}
                  </Typography>
                </StyledTh>
              )}
            </tr>
          ) : (
            <>
              {firstEmptyRow && (
                <StyledEmptyHeaderRow>
                  <th colSpan={columnsCount + 1}></th>
                </StyledEmptyHeaderRow>
              )}
            </>
          )}
        </StyledThead>
        <StyledTbody>
          {rows.map((row, rowIndex) => (
            <StyledRow key={rowIndex}>
              <StyledTd
                left
                style={{
                  paddingLeft: '20px',
                  paddingBottom: rowIndex + 1 === rows.length && !lastEmptyRow ? '14px' : 0,
                }}
              >
                {t(row.name)}
              </StyledTd>
              {row.columns.map((value, colIndex) => (
                <StyledTd
                  key={colIndex}
                  style={{
                    paddingRight:
                      colIndex + 1 === row.columns.length && !totalColumn ? '20px' : '5px',
                    paddingBottom: rowIndex + 1 === rows.length && !lastEmptyRow ? '14px' : 0,
                    width: '114px',
                  }}
                >
                  <Typography variant="body2">{value}</Typography>
                </StyledTd>
              ))}
              {totalColumn && (
                <StyledTd
                  key={row.columns.length}
                  style={{
                    paddingRight: '20px',
                    paddingBottom: rowIndex + 1 === rows.length && !lastEmptyRow ? '14px' : 0,
                  }}
                >
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    {row.columns.reduce((acc, val) => acc + val, 0)}
                  </Typography>
                </StyledTd>
              )}
            </StyledRow>
          ))}
          {lastEmptyRow && (
            <StyledRow
              key={rows.length}
              style={{
                minHeight: '42px',
                height: '42px',
              }}
            >
              <StyledTd
                left
                style={{
                  paddingLeft: '20px',
                  paddingBottom: '14px',
                }}
              >
                {' '}
              </StyledTd>
            </StyledRow>
          )}
        </StyledTbody>
        <tfoot>
          <StyledTotalRow>
            <StyledTotalTd isLabel left style={{ paddingLeft: '20px' }}>
              <Typography variant="body1">{t('global.total.label').toUpperCase()}</Typography>
            </StyledTotalTd>
            {columnTotals.map((total, index) => (
              <StyledTotalTd
                key={index}
                style={{
                  paddingRight:
                    index + 1 === columnTotals.length && !totalColumn ? '20px' : '5px',
                }}
              >
                <Typography variant="body1" sx={{ display: totalColumn ? 'none' : 'block' }}>
                  <b>{total}</b>
                </Typography>
              </StyledTotalTd>
            ))}
            {totalColumn && (
              <StyledTotalTd key={columnTotals.length} style={{ paddingRight: '20px' }}>
                <Typography variant="body1">
                  <b>{columnTotals.reduce((acc, val) => acc + val, 0)}</b>
                </Typography>
              </StyledTotalTd>
            )}
          </StyledTotalRow>
        </tfoot>
      </StyledTable>
    </StyledCard>
  );
};
